import { LocalizationService } from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { Component, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs';
import { HasSubscription } from 'src/ca-shared/has-subscription/has-subscription';
import { NotificationSettingCategoryModel } from 'src/ca-shared/notification-channel-matrix/models/notification-setting-category.model';
import { NotificationSettingItemModel } from 'src/ca-shared/notification-channel-matrix/models/notification-setting-item.model';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { RoleDto } from 'src/core/models/shared/role.dto';
import { PendingChangesService } from 'src/core/services/settings/pending-changes.service';
import { NotificationChannelSettingRequestModel } from './models/notification-channel-setting-request.model';
import { NotificationSettingsViewModel } from './models/notification-settings-view.model';
import { NotificationSettingsService } from './services/notification-settings.service';

@Component({
  selector: 'ca-notification-settings',
  templateUrl: './notification-settings.component.html',
  styleUrls: ['./notification-settings.component.scss'],
  standalone: false,
})
@AutoUnsubscribe()
export class NotificationSettingsComponent extends HasSubscription implements OnDestroy {
  selectedRole: RoleDto = null;

  categories: NotificationSettingCategoryModel[] = [];

  isLoading = false;
  isSaving = false;
  isDirty = false;

  // The matrix mutates the items it is given, so the untouched server state is kept
  // in a separate deep copy that never shares instances with the bound data.
  private pristineCategories: NotificationSettingCategoryModel[] = [];
  private loadedRole: RoleDto = null;
  private loadFailed = false;

  get isRoleSelected(): boolean {
    return this.selectedRole !== null && this.selectedRole !== undefined;
  }

  get hasCategories(): boolean {
    return this.categories.length > 0;
  }

  get showMatrix(): boolean {
    return this.isRoleSelected && !this.isLoading && !this.loadFailed && this.hasCategories;
  }

  get showLoadError(): boolean {
    return this.isRoleSelected && !this.isLoading && this.loadFailed;
  }

  get showEmptyState(): boolean {
    return this.isRoleSelected && !this.isLoading && !this.loadFailed && !this.hasCategories;
  }

  get isMatrixDisabled(): boolean {
    return this.isSaving;
  }

  get isSaveDisabled(): boolean {
    return this.isSaving || !this.isDirty;
  }

  get isCancelDisabled(): boolean {
    return this.isSaving || !this.isDirty;
  }

  constructor(
    private notificationSettingsService: NotificationSettingsService,
    private pendingChangesService: PendingChangesService,
    private confirmationService: ConfirmationService,
    private toasterService: ToasterService,
    private localizationService: LocalizationService,
  ) {
    super();
  }

  // "hasPendingChanges" is an application wide singleton and the tab host destroys
  // this component on every tab switch, so an unreleased flag would keep firing the
  // unsaved changes guard on unrelated screens for the rest of the session.
  ngOnDestroy(): void {
    this.setDirty(false);
  }

  onRoleChanged(role: RoleDto): void {
    const previousRole = this.loadedRole;

    // Keep the selector and the model in sync first, so a decline can push the
    // previous role back into the selector as a real model change.
    this.selectedRole = role;

    if (!this.isDirty) {
      this.applyRoleSelection(role);

      return;
    }

    this.confirmationService
      .warn('Settings::Notifications:UnsavedChangesOnRoleChange', '::Warning')
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.applyRoleSelection(role);

          return;
        }

        this.selectedRole = previousRole;
      });
  }

  onChanged(): void {
    this.setDirty(true);
  }

  onCancel(): void {
    // A brand new array reference is required, the matrix is OnPush and only
    // reacts to a changed "categories" input.
    this.categories = this.cloneCategories(this.pristineCategories);
    this.setDirty(false);
  }

  onSave(): void {
    if (!this.isRoleSelected || this.isSaving) {
      return;
    }

    const roleId = this.selectedRole.id;
    const request = this.buildRequest();

    this.isSaving = true;

    this.notificationSettingsService
      .updateByRole(roleId, request)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.pristineCategories = this.cloneCategories(this.categories);
          this.setDirty(false);
          this.toasterService.success(
            this.localizationService.instant('Settings::Notifications:Saved'),
          );
        },
        error: () => {
          this.isSaving = false;
        },
      });
  }

  private applyRoleSelection(role: RoleDto): void {
    this.loadedRole = role;
    this.setDirty(false);

    if (!role) {
      // Clearing the role abandons any in flight load, its response is dropped as
      // stale, so the spinner has to be released here.
      this.isLoading = false;
      this.categories = [];
      this.pristineCategories = [];
      this.loadFailed = false;

      return;
    }

    this.loadSettings(role.id);
  }

  private loadSettings(roleId: string): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.categories = [];
    this.pristineCategories = [];

    this.notificationSettingsService
      .getByRole(roleId)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .subscribe({
        next: (view: NotificationSettingsViewModel) => {
          if (this.isStaleResponse(roleId)) {
            return;
          }

          const loaded = view && view.categories ? view.categories : [];

          // Two independent deep copies: one for the baseline, one for the matrix.
          this.pristineCategories = this.cloneCategories(loaded);
          this.categories = this.cloneCategories(loaded);
          this.isLoading = false;
        },
        error: () => {
          if (this.isStaleResponse(roleId)) {
            return;
          }

          this.isLoading = false;
          this.loadFailed = true;
          this.categories = [];
          this.pristineCategories = [];
        },
      });
  }

  // Role changes are not serialised, so a slower response for a previously selected
  // role can still arrive last. Applying it would render that role's matrix under the
  // current role's label and a following save would write it onto the wrong role.
  private isStaleResponse(roleId: string): boolean {
    return !this.loadedRole || this.loadedRole.id !== roleId;
  }

  private buildRequest(): NotificationChannelSettingRequestModel[] {
    const request: NotificationChannelSettingRequestModel[] = [];

    this.categories.forEach(category => {
      (category.items ?? []).forEach(item => {
        const setting = new NotificationChannelSettingRequestModel();
        setting.notificationType = item.notificationType;
        setting.inAppEnabled = item.inAppEnabled;
        setting.emailEnabled = item.emailEnabled;

        request.push(setting);
      });
    });

    return request;
  }

  private cloneCategories(
    categories: NotificationSettingCategoryModel[],
  ): NotificationSettingCategoryModel[] {
    return (categories ?? []).map(category => {
      const clone = new NotificationSettingCategoryModel();
      clone.code = category.code;
      clone.displayName = category.displayName;
      clone.items = (category.items ?? []).map(item => this.cloneItem(item));

      return clone;
    });
  }

  private cloneItem(item: NotificationSettingItemModel): NotificationSettingItemModel {
    const clone = new NotificationSettingItemModel();
    clone.notificationType = item.notificationType;
    clone.displayName = item.displayName;
    clone.description = item.description;
    clone.supportsInApp = item.supportsInApp;
    clone.supportsEmail = item.supportsEmail;
    clone.inAppEnabled = item.inAppEnabled;
    clone.emailEnabled = item.emailEnabled;

    return clone;
  }

  private setDirty(isDirty: boolean): void {
    this.isDirty = isDirty;
    this.pendingChangesService.hasPendingChanges = isDirty;
  }
}
